/**
 * Knowledge RPC 契约（workbench knowledge 域，24 端点）。
 *
 * payload schema 直接复用 @crm/domain/knowledge-forms（serverFn/RPC 双入口 SSOT），
 * success 使用浏览器读模型（Date 字段 wire=ISO string，decode 回 Date）；
 * 向量、查重状态、后台写入来源及租约留在服务端，不构成编辑/阅读契约。
 *
 * 端点权限映射（实现层 requirePermissionsEffect 用）：
 * - getKnowledgeEntryById / getKnowledgeEntryStats / getKnowledgeEntriesPaginated
 *   → KNOWLEDGE_ENTRY_READ_ALL
 * - getTagCounts → 仅认证（access() 空调用）
 * - createManualDraft / updateKnowledgeEntry / saveKnowledgeEntrySnapshot
 *   → KNOWLEDGE_ENTRY_WRITE_ALL / KNOWLEDGE_ENTRY_WRITE_ASSIGNED
 * - reviewKnowledgeEntry / checkEntryDuplicates → KNOWLEDGE_ENTRY_REVIEW_ALL
 * - claimKnowledgeEntry → KNOWLEDGE_ENTRY_CLAIM_ALL
 * - publishKnowledgeEntry → KNOWLEDGE_ENTRY_PUBLISH_ALL
 * - deleteKnowledgeEntry → KNOWLEDGE_ENTRY_DELETE_ALL / KNOWLEDGE_ENTRY_WRITE_ASSIGNED
 * - getKnowledgeEntryCourses / getKnowledgeEntryTags → KNOWLEDGE_ENTRY_READ_ALL /
 *   KNOWLEDGE_TAG_READ_ALL
 * - add/remove/set KnowledgeEntryCourse|Tag → KNOWLEDGE_ENTRY_WRITE_ALL / WRITE_ASSIGNED
 * - getKnowledgeTags → KNOWLEDGE_TAG_READ_ALL
 * - create/updateKnowledgeTag → KNOWLEDGE_TAG_WRITE_ALL
 * - deleteKnowledgeTag → KNOWLEDGE_TAG_DELETE_ALL
 */
import * as Rpc from "effect/unstable/rpc/Rpc";
import * as RpcGroup from "effect/unstable/rpc/RpcGroup";
import {
  addKnowledgeEntryCourseInputSchema,
  addKnowledgeEntryTagInputSchema,
  checkEntryDuplicatesInputSchema,
  claimKnowledgeEntryInputSchema,
  createKnowledgeTagInputSchema,
  createManualDraftInputSchema,
  deleteKnowledgeEntryInputSchema,
  deleteKnowledgeTagInputSchema,
  getKnowledgeEntriesPaginatedInputSchema,
  getKnowledgeEntryByIdInputSchema,
  publishKnowledgeEntryInputSchema,
  removeKnowledgeEntryCourseInputSchema,
  removeKnowledgeEntryTagInputSchema,
  reviewKnowledgeEntryInputSchema,
  saveKnowledgeEntrySnapshotInputSchema,
  setKnowledgeEntryCoursesInputSchema,
  setKnowledgeEntryTagsInputSchema,
  undefinedInputSchema,
  updateKnowledgeEntryInputSchema,
  updateKnowledgeTagInputSchema,
} from "@crm/domain/knowledge-forms";
import { Schema } from "effect";
import { asContractSchema, RpcAppError } from "../errors";
import { RpcAuthMiddleware } from "../middleware";

// =============================================================================
// Success schemas（与 knowledge.effects.ts 业务返回类型一致）
// =============================================================================

const entryStatusLiterals = Schema.Literals(["pending_review", "approved", "draft", "published"]);

/** 编辑/阅读所需的条目详情，后台查重数据不影响正文的可读性。 */
export const KnowledgeEntryDetailSchema = Schema.Struct({
  id: Schema.String,
  semanticRevision: Schema.Finite,
  title: Schema.String,
  sourceMessage: Schema.NullOr(Schema.String),
  content: Schema.NullOr(Schema.String),
  status: entryStatusLiterals,
  reviewedBy: Schema.NullOr(Schema.String),
  reviewedAt: Schema.NullOr(Schema.DateFromString),
  claimedBy: Schema.NullOr(Schema.String),
  claimedAt: Schema.NullOr(Schema.DateFromString),
  publishedAt: Schema.NullOr(Schema.DateFromString),
  viewCount: Schema.Finite,
  analysisDate: Schema.NullOr(Schema.DateFromString),
  confidence: Schema.NullOr(Schema.Finite),
  createdAt: Schema.NullOr(Schema.DateFromString),
  updatedAt: Schema.NullOr(Schema.DateFromString),
});
export type KnowledgeEntryDetailWire = Schema.Schema.Type<typeof KnowledgeEntryDetailSchema>;

/** 知识标签行（精确契约：Date 字段 wire=ISO string，decode 回 Date） */
export const KnowledgeTagRowSchema = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  keywords: Schema.NullOr(Schema.mutable(Schema.Array(Schema.String))),
  color: Schema.NullOr(Schema.String),
  sortOrder: Schema.Finite,
  createdBy: Schema.NullOr(Schema.String),
  createdAt: Schema.NullOr(Schema.DateFromString),
  updatedAt: Schema.NullOr(Schema.DateFromString),
});
export type KnowledgeTagRowWire = Schema.Schema.Type<typeof KnowledgeTagRowSchema>;

/** 条目-课程关联行 */
export const KnowledgeEntryCourseRowSchema = Schema.Struct({
  entryId: Schema.String,
  courseId: Schema.String,
});
export type KnowledgeEntryCourseRowWire = Schema.Schema.Type<typeof KnowledgeEntryCourseRowSchema>;

/** 条目-标签关联行 */
export const KnowledgeEntryTagRowSchema = Schema.Struct({
  entryId: Schema.String,
  tagId: Schema.String,
});
export type KnowledgeEntryTagRowWire = Schema.Schema.Type<typeof KnowledgeEntryTagRowSchema>;

/** getKnowledgeEntryStats 返回（各状态计数） */
export const KnowledgeEntryStatsSchema = Schema.Struct({
  pendingReview: Schema.Finite,
  approved: Schema.Finite,
  drafts: Schema.Finite,
  published: Schema.Finite,
});
export type KnowledgeEntryStatsWire = Schema.Schema.Type<typeof KnowledgeEntryStatsSchema>;

/** getKnowledgeEntriesPaginated 单条（富化后，不含 content/embedding） */
export const KnowledgeEntriesPaginatedItemSchema = Schema.Struct({
  id: Schema.String,
  title: Schema.String,
  status: entryStatusLiterals,
  sourceMessage: Schema.NullOr(Schema.String),
  claimedBy: Schema.NullOr(Schema.String),
  claimedAt: Schema.NullOr(Schema.DateFromString),
  publishedAt: Schema.NullOr(Schema.DateFromString),
  viewCount: Schema.Finite,
  analysisDate: Schema.NullOr(Schema.DateFromString),
  updatedAt: Schema.NullOr(Schema.DateFromString),
  hasEmbedding: Schema.Boolean,
  authorName: Schema.NullOr(Schema.String),
  tagIds: Schema.mutable(Schema.Array(Schema.String)),
  tagNames: Schema.mutable(Schema.Array(Schema.String)),
  courseIds: Schema.mutable(Schema.Array(Schema.String)),
  courseNames: Schema.mutable(Schema.Array(Schema.String)),
});

/** getKnowledgeEntriesPaginated 返回 */
export const KnowledgeEntriesPaginatedResultSchema = Schema.Struct({
  items: Schema.mutable(Schema.Array(KnowledgeEntriesPaginatedItemSchema)),
  totalCount: Schema.Finite,
  hasMore: Schema.Boolean,
  currentPage: Schema.Finite,
});
export type KnowledgeEntriesPaginatedResultWire = Schema.Schema.Type<
  typeof KnowledgeEntriesPaginatedResultSchema
>;

/** getTagCounts 返回（标签计数 + 已发布总数） */
export const KnowledgeTagCountsSchema = Schema.Struct({
  tags: Schema.mutable(
    Schema.Array(
      Schema.Struct({
        id: Schema.String,
        name: Schema.String,
        count: Schema.Finite,
      }),
    ),
  ),
  totalArticles: Schema.Finite,
});
export type KnowledgeTagCountsWire = Schema.Schema.Type<typeof KnowledgeTagCountsSchema>;

/** reviewKnowledgeEntry 删除分支返回 */
export const ReviewKnowledgeEntryDeletedSchema = Schema.Struct({
  success: Schema.Literal(true),
  deleted: Schema.Literal(true),
});

/** checkEntryDuplicates 返回 */
export const CheckEntryDuplicatesSchema = Schema.Struct({
  entryId: Schema.String,
  entryTitle: Schema.String,
  hasDuplicates: Schema.Boolean,
  duplicates: Schema.mutable(
    Schema.Array(
      Schema.Struct({
        id: Schema.String,
        title: Schema.String,
        sourceMessage: Schema.NullOr(Schema.String),
        status: Schema.String,
        score: Schema.Finite,
      }),
    ),
  ),
});
export type CheckEntryDuplicatesWire = Schema.Schema.Type<typeof CheckEntryDuplicatesSchema>;

/** 通用 { success: true } 返回 */
const successTrueSchema = Schema.Struct({ success: Schema.Literal(true) });

/**
 * 知识条目读写（12 端点）
 */
export class KnowledgeEntriesRpc extends RpcGroup.make(
  Rpc.make("getKnowledgeEntryById", {
    payload: asContractSchema(getKnowledgeEntryByIdInputSchema),
    success: Schema.NullOr(KnowledgeEntryDetailSchema),
    error: RpcAppError,
  }),
  Rpc.make("getKnowledgeEntryStats", {
    payload: asContractSchema(undefinedInputSchema),
    success: KnowledgeEntryStatsSchema,
    error: RpcAppError,
  }),
  Rpc.make("getKnowledgeEntriesPaginated", {
    payload: asContractSchema(getKnowledgeEntriesPaginatedInputSchema),
    success: KnowledgeEntriesPaginatedResultSchema,
    error: RpcAppError,
  }),
  Rpc.make("getTagCounts", {
    payload: asContractSchema(undefinedInputSchema),
    success: KnowledgeTagCountsSchema,
    error: RpcAppError,
  }),
  Rpc.make("createManualDraft", {
    payload: asContractSchema(createManualDraftInputSchema),
    success: KnowledgeEntryDetailSchema,
    error: RpcAppError,
  }),
  Rpc.make("reviewKnowledgeEntry", {
    payload: asContractSchema(reviewKnowledgeEntryInputSchema),
    success: Schema.Union([KnowledgeEntryDetailSchema, ReviewKnowledgeEntryDeletedSchema]),
    error: RpcAppError,
  }),
  Rpc.make("claimKnowledgeEntry", {
    payload: asContractSchema(claimKnowledgeEntryInputSchema),
    success: KnowledgeEntryDetailSchema,
    error: RpcAppError,
  }),
  Rpc.make("updateKnowledgeEntry", {
    payload: asContractSchema(updateKnowledgeEntryInputSchema),
    success: KnowledgeEntryDetailSchema,
    error: RpcAppError,
  }),
  Rpc.make("saveKnowledgeEntrySnapshot", {
    payload: asContractSchema(saveKnowledgeEntrySnapshotInputSchema),
    success: KnowledgeEntryDetailSchema,
    error: RpcAppError,
  }),
  Rpc.make("publishKnowledgeEntry", {
    payload: asContractSchema(publishKnowledgeEntryInputSchema),
    success: KnowledgeEntryDetailSchema,
    error: RpcAppError,
  }),
  Rpc.make("deleteKnowledgeEntry", {
    payload: asContractSchema(deleteKnowledgeEntryInputSchema),
    success: successTrueSchema,
    error: RpcAppError,
  }),
  Rpc.make("checkEntryDuplicates", {
    payload: asContractSchema(checkEntryDuplicatesInputSchema),
    success: CheckEntryDuplicatesSchema,
    error: RpcAppError,
  }),
).middleware(RpcAuthMiddleware) {}

/**
 * 知识条目-课程关联（4 端点）
 */
export class KnowledgeEntryCoursesRpc extends RpcGroup.make(
  Rpc.make("getKnowledgeEntryCourses", {
    payload: asContractSchema(undefinedInputSchema),
    success: Schema.mutable(Schema.Array(KnowledgeEntryCourseRowSchema)),
    error: RpcAppError,
  }),
  Rpc.make("addKnowledgeEntryCourse", {
    payload: asContractSchema(addKnowledgeEntryCourseInputSchema),
    // 已存在时返回既有行数组，新增时返回单行
    success: Schema.Union([
      KnowledgeEntryCourseRowSchema,
      Schema.mutable(Schema.Array(KnowledgeEntryCourseRowSchema)),
    ]),
    error: RpcAppError,
  }),
  Rpc.make("removeKnowledgeEntryCourse", {
    payload: asContractSchema(removeKnowledgeEntryCourseInputSchema),
    success: successTrueSchema,
    error: RpcAppError,
  }),
  Rpc.make("setKnowledgeEntryCourses", {
    payload: asContractSchema(setKnowledgeEntryCoursesInputSchema),
    // effect 原样回传 input.courseIds（readonly）
    success: Schema.Struct({
      success: Schema.Literal(true),
      courseIds: Schema.Array(Schema.String),
    }),
    error: RpcAppError,
  }),
).middleware(RpcAuthMiddleware) {}

/**
 * 知识条目-标签关联（4 端点）
 */
export class KnowledgeEntryTagsRpc extends RpcGroup.make(
  Rpc.make("getKnowledgeEntryTags", {
    payload: asContractSchema(undefinedInputSchema),
    success: Schema.mutable(Schema.Array(KnowledgeEntryTagRowSchema)),
    error: RpcAppError,
  }),
  Rpc.make("addKnowledgeEntryTag", {
    payload: asContractSchema(addKnowledgeEntryTagInputSchema),
    // 实现 effect 理论边界可返回 undefined（DB 不一致），RPC 层规整为 null
    success: Schema.NullOr(KnowledgeEntryTagRowSchema),
    error: RpcAppError,
  }),
  Rpc.make("removeKnowledgeEntryTag", {
    payload: asContractSchema(removeKnowledgeEntryTagInputSchema),
    success: successTrueSchema,
    error: RpcAppError,
  }),
  Rpc.make("setKnowledgeEntryTags", {
    payload: asContractSchema(setKnowledgeEntryTagsInputSchema),
    // effect 原样回传 input.tagIds（readonly）
    success: Schema.Struct({
      success: Schema.Literal(true),
      tagIds: Schema.Array(Schema.String),
    }),
    error: RpcAppError,
  }),
).middleware(RpcAuthMiddleware) {}

/**
 * 知识标签 CRUD（4 端点）
 */
export class KnowledgeTagsRpc extends RpcGroup.make(
  Rpc.make("getKnowledgeTags", {
    payload: asContractSchema(undefinedInputSchema),
    success: Schema.mutable(Schema.Array(KnowledgeTagRowSchema)),
    error: RpcAppError,
  }),
  Rpc.make("createKnowledgeTag", {
    payload: asContractSchema(createKnowledgeTagInputSchema),
    success: KnowledgeTagRowSchema,
    error: RpcAppError,
  }),
  Rpc.make("updateKnowledgeTag", {
    payload: asContractSchema(updateKnowledgeTagInputSchema),
    success: KnowledgeTagRowSchema,
    error: RpcAppError,
  }),
  Rpc.make("deleteKnowledgeTag", {
    payload: asContractSchema(deleteKnowledgeTagInputSchema),
    success: successTrueSchema,
    error: RpcAppError,
  }),
).middleware(RpcAuthMiddleware) {}
